import { parsePublishableKey } from '@clerk/shared/keys';

/**
 * The Clerk Account Portal host that belongs to a Frontend API host, by Clerk's naming:
 * production `clerk.<domain>` → `accounts.<domain>`, development
 * `<slug>.clerk.accounts.dev` → `<slug>.accounts.dev`. Null for any other shape.
 */
function accountPortalHost(frontendApi: string): string | null {
  const dev = /^([a-z0-9-]+)\.clerk\.accounts\.dev$/.exec(frontendApi);
  if (dev) return `${dev[1]}.accounts.dev`;
  const prod = /^clerk\.(.+)$/.exec(frontendApi);
  return prod ? `accounts.${prod[1]}` : null;
}

/**
 * Where a signed-in user may be sent from the auth pages via `redirect_url`.
 *
 * Clerk's OAuth authorization server (used by the MCP connector in Claude.ai /
 * ChatGPT) sends a user who needs to sign in to `/login?redirect_url=<url>`, where
 * `<url>` is the consent page on the Account Portal
 * (`https://accounts.<domain>/oauth-consent?...`, seen live in prod on 2026-10-06) or
 * an `/oauth/authorize` URL on the Frontend API. After sign-in the user must reach
 * that URL to consent and return to the client. Only this app's origin and this
 * environment's Clerk Frontend API and Account Portal origins are accepted: anything
 * else would make /login an open redirect.
 */
export function allowedRedirectOrigins(appOrigin: string, publishableKey: string): string[] {
  const origins = [new URL(appOrigin).origin];
  const frontendApi = parsePublishableKey(publishableKey)?.frontendApi;
  if (frontendApi) {
    origins.push(new URL(`https://${frontendApi}`).origin);
    const accounts = accountPortalHost(frontendApi);
    if (accounts) origins.push(new URL(`https://${accounts}`).origin);
  }
  return origins;
}

/**
 * Reads `redirect_url` from the query string, or from Clerk's hash form
 * (`#/?redirect_url=...`) when the query has none.
 */
function readRedirectParam(location: { search: string; hash: string }): string | null {
  const fromQuery = new URLSearchParams(location.search).get('redirect_url');
  if (fromQuery !== null) return fromQuery;
  const queryStart = location.hash.indexOf('?');
  if (queryStart === -1) return null;
  return new URLSearchParams(location.hash.slice(queryStart + 1)).get('redirect_url');
}

/**
 * The `redirect_url` of the current location as an absolute URL, or null when it
 * is absent, malformed, or not on an allowed origin. The returned value is the
 * parser's own serialization, so the URL that was checked is the URL navigated to.
 */
export function safeRedirectUrl(
  location: { search: string; hash: string },
  appOrigin: string,
  publishableKey: string,
): string | null {
  const raw = readRedirectParam(location);
  if (!raw) return null;
  let target: URL;
  try {
    target = new URL(raw, appOrigin);
  } catch {
    return null;
  }
  // Built from protocol + host instead of `URL.origin`: a `blob:` URL reports the
  // origin of its creator, so `blob:<app origin>/<id>` would pass an origin check.
  const origin = `${target.protocol}//${target.host}`;
  return allowedRedirectOrigins(appOrigin, publishableKey).includes(origin) ? target.href : null;
}
